import React, { useState } from 'react'
import { Table } from 'components/Ui'
import { TextInput, Select, DatePicker } from 'components/Form'
import dayjs from 'dayjs'

// ConsignmentFilters component
const ConsignmentFilters = ({ tableData, onChange }) => {
    // Set default created_date_from to 14 days ago
    const defaultFromDate = dayjs().subtract(14, 'day').format('YYYY-MM-DD')

    const [filters, setFilters] = useState(() => {
        const existingFilters = tableData.filters || {}
        return {
            company_name: existingFilters.company_name || '',
            carrier_and_service_name: existingFilters.carrier_and_service_name || '',
            tracking_status_name: existingFilters.tracking_status_name || '',
            created_date_from: existingFilters.created_date_from || defaultFromDate,
            created_date_to: existingFilters.created_date_to || '',
            completed_date_status:
                existingFilters.completed_date_status !== undefined ? existingFilters.completed_date_status : null,
        }
    })

    const handleFilterChange = (key, value) => {
        const newFilters = { ...filters, [key]: value }
        setFilters(newFilters)

        const newTableData = { ...tableData, filters: newFilters, pageIndex: 1 }
        onChange(newTableData)
    }

    const handleClearFilters = () => {
        const clearedFilters = {
            company_name: '',
            carrier_and_service_name: '',
            tracking_status_name: '',
            created_date_from: defaultFromDate,
            created_date_to: '',
            completed_date_status: null,
        }
        setFilters(clearedFilters)
        const newTableData = { ...tableData, filters: clearedFilters, pageIndex: 1 }
        onChange(newTableData)
    }

    const completedDateOptions = [
        { label: 'All', value: '' },
        { label: 'Yes', value: 'true' },
        { label: 'No', value: 'false' },
    ]

    return (
        <div className="mb-6 p-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            {/* Filter Header */}
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Filter Consignments</h3>
                <button
                    onClick={handleClearFilters}
                    className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors duration-200"
                >
                    Clear All
                </button>
            </div>

            {/* Search Filters Section */}
            <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Search Filters</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Company Name</label>
                        <TextInput
                            id="company_name"
                            name="company_name"
                            placeholder="Enter company name..."
                            value={filters.company_name || ''}
                            onChange={e => handleFilterChange('company_name', e.target.value)}
                            className="w-full"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Carrier & Service
                        </label>
                        <TextInput
                            id="carrier_and_service_name"
                            name="carrier_and_service_name"
                            placeholder="Enter carrier or service..."
                            value={filters.carrier_and_service_name || ''}
                            onChange={e => handleFilterChange('carrier_and_service_name', e.target.value)}
                            className="w-full"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Tracking Status</label>
                        <TextInput
                            id="tracking_status_name"
                            name="tracking_status_name"
                            placeholder="Enter tracking status..."
                            value={filters.tracking_status_name || ''}
                            onChange={e => handleFilterChange('tracking_status_name', e.target.value)}
                            className="w-full"
                        />
                    </div>
                </div>
            </div>

            {/* Date & Status Filters Section */}
            <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Date & Status Filters</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Created Date Range
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <DatePicker
                                    id="created_date_from"
                                    name="created_date_from"
                                    placeholder="From date (default: 14 days ago)"
                                    value={filters.created_date_from || ''}
                                    onChange={e => handleFilterChange('created_date_from', e.target.value)}
                                    className="w-full"
                                />
                            </div>
                            <div>
                                <DatePicker
                                    id="created_date_to"
                                    name="created_date_to"
                                    placeholder="To date"
                                    value={filters.created_date_to || ''}
                                    onChange={e => handleFilterChange('created_date_to', e.target.value)}
                                    className="w-full"
                                />
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Completion Status
                        </label>
                        <Select
                            id="completed_date_status"
                            name="completed_date_status"
                            placeholder="Select completion status"
                            value={
                                filters.completed_date_status !== null && filters.completed_date_status !== undefined
                                    ? filters.completed_date_status.toString()
                                    : ''
                            }
                            options={completedDateOptions}
                            onChange={e => {
                                const value = e.target.value === '' ? null : e.target.value === 'true'
                                handleFilterChange('completed_date_status', value)
                            }}
                            className="w-full"
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

// ConsignmentTab component
const ConsignmentTab = ({
    consignmentData,
    consignmentTableData,
    columns,
    loading,
    company,
    dispatch,
    setConsignmentTableData,
}) => {
    return (
        <>
            <ConsignmentFilters tableData={consignmentTableData} onChange={e => dispatch(setConsignmentTableData(e))} />
            <Table
                key={`consignments-${company}-${consignmentData?.length}`}
                loading={loading}
                filter={false}
                datas={consignmentData}
                columns={columns}
                pagingData={consignmentTableData}
                onChange={e => dispatch(setConsignmentTableData(e))}
            />
        </>
    )
}

export default ConsignmentTab
